import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import { Skeleton } from '@/components/ui/Skeleton'
import { fetchRatingForTrip } from './api'
import { isPositiveTag, moodLabel, tagLabel } from './describe'
import { RatingStars } from './RatingStars'
import styles from './RatingsPage.module.css'

interface TripRatingPanelProps {
  tripId: number
  /** The trip's status; the rating is only asked for once the trip is over. */
  status: string | null
}

/**
 * The customer's verdict on one trip, inside the trip drawer.
 *
 * Fetched here rather than carried on the trip row: most trips on the Trips
 * page are not finished, and the ones that are would each need a join for a
 * field the operator only reads when they open the drawer. An unrated trip
 * says so plainly — it is the normal case, not a gap.
 */
export function TripRatingPanel({ tripId, status }: TripRatingPanelProps) {
  const finished = status === 'COMPLETED'

  const ratingQuery = useQuery({
    queryKey: queryKeys.ratings.forTrip(tripId),
    queryFn: () => fetchRatingForTrip(tripId),
    enabled: finished,
  })

  if (!finished) {
    return <p className={styles.muted}>The customer can rate this trip once it has finished.</p>
  }

  if (ratingQuery.isLoading) {
    return <Skeleton height={56} radius="var(--radius-md)" />
  }

  if (ratingQuery.isError) {
    return <p className={styles.muted}>The rating could not be loaded right now.</p>
  }

  const rating = ratingQuery.data
  if (!rating) {
    return <p className={styles.muted}>The customer has not rated this trip.</p>
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeadline}>
        <RatingStars value={rating.rating} size="md" />
        <span className={cn(styles.mood, rating.low && styles.moodLow)}>{moodLabel(rating.rating)}</span>
        <span className={styles.muted}>· {formatDateTime(rating.createdAt)}</span>
      </div>
      {rating.tags.length > 0 ? (
        <div className={styles.tagRow}>
          {rating.tags.map((tag) => (
            <span key={tag} className={cn(styles.tag, isPositiveTag(tag) ? styles.tagPositive : styles.tagNegative)}>
              {tagLabel(tag)}
            </span>
          ))}
        </div>
      ) : null}
      {rating.comment ? <blockquote className={styles.quote}>{rating.comment}</blockquote> : null}
      {rating.driverId != null ? (
        <Link to={`/ratings?driverId=${rating.driverId}`} className={styles.drawerLink}>
          All ratings for this driver
        </Link>
      ) : null}
    </div>
  )
}
